<template>
  <div>
    <div class="navigation">
      <div class="navigation_bottom">
        <img class="ai_assistant" ref="ai_assistant"
          src="https://mj-uploads.oss-cn-shenzhen.aliyuncs.com/public/aiDesign/web/AI_Ass/AI_Ass.png" alt="">
        <span ref="scroll_bottom" class="scroll_bottom iconfont" @click="scrollToBottom">&#xe685; 回到底部</span>
      </div>
      <div class="navigation_items" ref="navigation_items">
        <div class="main_input" ref="main_input">
          <!-- 上传区域 -->
          <div class="upload-row">
            <!-- 首帧图片上传区域 -->
            <div class="upload-section" @click="handleStartImageUpload"
              @dragover.prevent="handleDragOver($event, 'start')"
              @dragleave.prevent="handleDragLeave('start')"
              @drop.prevent="handleDrop($event, 'start')"
              :class="{ 'drag-over': isDragging === 'start' }">
              <div v-if="!startImage" class="upload-placeholder">
                <span class="upload-plus">+</span>
                <span class="upload-label">首帧</span>
              </div>
              <div v-else class="uploaded-preview">
                <img :src="startImage" alt="首帧">
                <div class="remove-btn" @click.stop="removeStartImage">×</div>
              </div>
            </div>

            <!-- 尾帧图片上传区域 - 仅在首尾帧模式显示 -->
            <div v-if="selectedFrameMode === '首尾帧'" class="upload-section" @click="handleEndImageUpload"
              @dragover.prevent="handleDragOver($event, 'end')"
              @dragleave.prevent="handleDragLeave('end')"
              @drop.prevent="handleDrop($event, 'end')"
              :class="{ 'drag-over': isDragging === 'end' }">
              <div v-if="!endImage" class="upload-placeholder">
                <span class="upload-plus">+</span>
                <span class="upload-label">尾帧</span>
              </div>
              <div v-else class="uploaded-preview">
                <img :src="endImage" alt="尾帧">
                <div class="remove-btn" @click.stop="removeEndImage">×</div>
              </div>
            </div>
          </div>

          <!-- 输入框区域 -->
          <div class="input-area">
            <textarea name="" id="" placeholder="描述你想要生成的视频效果..."
              v-model="textarea"
              @focus="handleTextareaFocus"
              @input="handleTextareaInput"
              @blur="handleTextareaBlur"
              @click="handleTextareaClick"
              @keydown="handleTextareaKeydown"
              :disabled="processing"></textarea>
            <button ref="up" class="sent iconfont" :class="{ 'has_input': canSend }" @click.stop="startTask" v-show="!scrollStatus" :disabled="!canSend || processing">
              <span v-if="!processing">&#xe688;</span>
              <span v-else class="rotating">&#xe6a4;</span>
            </button>
          </div>

          <!-- 底部功能按钮行 -->
          <div class="video-options" @click.stop>
            <!-- 视频时长选择 -->
            <div class="option-dropdown" @click.stop="toggleDurationMenu">
              <span class="option-label">时长</span>
              <span class="option-value">{{ selectedDuration }}</span>
              <span class="iconfont arrow"></span>
              <div v-show="showDurationMenu" class="dropdown-menu" @click.stop>
                <div
                  v-for="duration in durationOptions"
                  :key="duration"
                  class="menu-item"
                  :class="{ active: selectedDuration === duration }"
                  @click="selectDuration(duration)"
                >
                  {{ duration }}
                </div>
              </div>
            </div>

            <!-- 帧数控制方式选择 -->
            <div class="option-dropdown" @click.stop="toggleFrameMenu">
              <span class="option-label">帧数控制</span>
              <span class="option-value">{{ selectedFrameMode }}</span>
              <span class="iconfont arrow"></span>
              <div v-show="showFrameMenu" class="dropdown-menu" @click.stop>
                <div
                  v-for="mode in frameModeOptions"
                  :key="mode"
                  class="menu-item"
                  :class="{ active: selectedFrameMode === mode }"
                  @click="selectFrameMode(mode)"
                >
                  {{ mode }}
                </div>
              </div>
            </div>

            <!-- 画面大小选择 -->
            <div class="option-dropdown" @click.stop="toggleResolutionMenu">
              <span class="option-label">画面大小</span>
              <span class="option-value">{{ selectedResolution }}</span>
              <span class="iconfont arrow"></span>
              <div v-show="showResolutionMenu" class="dropdown-menu" @click.stop>
                <div
                  v-for="resolution in resolutionOptions"
                  :key="resolution"
                  class="menu-item"
                  :class="{ active: selectedResolution === resolution }"
                  @click="selectResolution(resolution)"
                >
                  {{ resolution }}
                </div>
              </div>
            </div>

            <!-- 风格选择 -->
            <div class="style-options">
              <div
                v-for="style in styleOptions"
                :key="style"
                class="style-btn"
                :class="{ active: selectedStyle === style }"
                @click.stop="selectStyle(style)"
              >
                {{ style }}
              </div>
            </div>
          </div>
        </div>
        <!-- 放大后的发送按钮（绝对定位） -->
        <button ref="down" class="sent_expanded iconfont" :class="{ 'has_input': canSend }" @click.stop="startTask" v-show="scrollStatus" :disabled="!canSend || processing">
          <span v-if="!processing">&#xe688;</span>
          <span v-else class="rotating">&#xe6a4;</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Message } from 'element-ui'
import { getOssClient } from '@/utils/oss'
import { v4 as uuidv4 } from 'uuid'
import { genPrompt } from '@/api/promptVideo'
import { genOssImage } from '@/utils/ossPath'
import { getLastUsedBaseUrl, cudaIndexNum } from '@/utils/comfyUiVideoRequest'
import axios from 'axios'
import cuda from '@/utils/checkCuda'
import videoWorkflow from '@/api/json_api/首尾帧图生视频.json'
import singleFrameWorkflow from '@/api/json_api/首帧图生视频.json'
import { getTaskEnd, getTaskStart } from '@/api/user'
import { VIDEO_GENERATION } from '@/enums/common/modules'
import { getImageSizeByLoading } from '@/utils/helper/function'

export default {
  name: 'NaviGation_Video',
  props: {
    initialChatHistory: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      textarea: '',
      startImage: '', // 首帧图片URL
      endImage: '', // 尾帧图片URL
      processing: false,
      videoChatHistory: [],
      scrollStatus: false,
      // 视频时长选项
      selectedDuration: '3s',
      durationOptions: ['3s', '5s', '8s'],
      showDurationMenu: false,
      // 帧数控制方式选项
      selectedFrameMode: '首尾帧',
      frameModeOptions: ['首尾帧', '首帧', '智能多帧'],
      showFrameMenu: false,
      // 画面大小选项
      selectedResolution: '720',
      resolutionOptions: ['720', '1080'],
      showResolutionMenu: false,
      // 风格选项
      selectedStyle: '常规类',
      styleOptions: ['常规类', '刷划类', '滴落类'],
      // WebSocket
      ws: null,
      clientId: '',
      // 防止重复获取视频标志
      videoFetched: false,
      // 当前使用的工作流类型 (用于确定输出节点)
      currentWorkflowType: '首尾帧', // '首尾帧' or '首帧'
      // 任务日志ID
      taskLogId: null,
      // 节点名称映射
      nodeNameMap: {
        // 通用节点（首尾帧和首帧共用）
        1: 'Resize image',
        2: 'Load text encoder',
        7: 'Video decode',
        8: 'Encode image vision',
        9: 'Select LoRA (high noise)',
        10: 'Select LoRA (low noise)',
        11: 'Load text encoder',
        12: 'Set block swap (high)',
        14: 'Configure block swap (high)',
        16: 'Encode text',
        17: 'Set block swap (low)',
        18: 'Configure block swap (low)',
        19: 'Apply LoRA (high)',
        20: 'Apply LoRA (low)',
        22: 'Load model (high noise)',
        23: 'Load vision encoder',
        24: 'Generate video (low)',
        27: 'Generate video (high)',
        28: 'Video decode',
        33: 'Process text',
        34: 'Prompt text',
        37: 'Process image embedding',
        38: 'Load VAE',
        39: 'Configure block swap',
        50: 'Scale image',
        51: 'Generate seed',
        54: 'Set duration',
        57: 'Calculate frames',
        58: 'Build sampler',
        60: 'Configure sampler',
        61: 'Load VAE',
        69: 'Get image info',
        71: 'Load model (low noise)',
        75: 'Upload to OSS',
        76: 'Load start frame',
        77: 'Load end frame',
        78: 'Prepare output',
        79: 'Apply LoRA (low)',
        80: 'Apply LoRA (high)',
        89: 'Encode image to video',
        90: 'Generate video (low)',
        91: 'Calculate steps',
        92: 'Set block swap (high)',
        93: 'Set block swap (low)',
        95: 'Create CFG schedule',
        99: 'Scale image',
        102: 'Set resolution',
        104: 'Configure block swap (high)',
        105: 'Configure block swap (low)',
        133: 'Set duration',
        134: 'Calculate frames',
        135: 'Calculate steps mid',
        136: 'Set total steps',
        142: 'Prompt text',
        143: 'Select LoRA (low)',
        144: 'Select LoRA (high)',
        145: 'Load start frame',
        146: 'Upload to OSS',
        147: 'Clean GPU memory'
      },
      currentExecutingNode: '', // 当前执行的节点名称
      isDragging: null // 拖拽状态：'start' / 'end' / null
    }
  },
  computed: {
    canSend() {
      // 首尾帧模式需要两张图片，首帧模式只需要首帧
      if (this.selectedFrameMode === '首尾帧') {
        return this.textarea.trim() !== '' && this.startImage !== '' && this.endImage !== ''
      } else if (this.selectedFrameMode === '首帧') {
        return this.textarea.trim() !== '' && this.startImage !== ''
      }
      return false
    }
  },
  created() {
    if (this.initialChatHistory && this.initialChatHistory.length > 0) {
      this.videoChatHistory = [...this.initialChatHistory]
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyPaste)
    // 点击外部关闭下拉菜单
    document.addEventListener('click', this.closeAllMenus)
    // 监听重新生成事件
    this.$root.$on('video-regenerate', this.handleRegenerateFromParent)
    // 监听重新填入事件
    this.$root.$on('video-refill', this.handleRefillFromParent)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyPaste)
    document.removeEventListener('click', this.closeAllMenus)
    // 移除事件监听
    this.$root.$off('video-regenerate', this.handleRegenerateFromParent)
    this.$root.$off('video-refill', this.handleRefillFromParent)
  },
  methods: {
    // 处理首帧图片上传
    handleStartImageUpload() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (file) {
          await this.processImage(file, 'start')
        }
      }
      input.click()
    },

    // 处理尾帧图片上传
    handleEndImageUpload() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (file) {
          await this.processImage(file, 'end')
        }
      }
      input.click()
    },

    // 处理图片文件
    async processImage(file, type) {
      try {
        const label = type === 'start' ? '首帧' : '尾帧'
        Message.info(`正在上传${label}图片...`)
        const ossClient = await getOssClient()
        const tmp = '/input/'
        const fileName = `video_${type}_${Date.now()}_${file.name}`
        const result = await ossClient.put(tmp + fileName, file)

        console.log('OSS上传结果:', result)

        if (result.name) {
          // 使用 result.name 构建 ComfyUI 可访问的 URL
          const imageUrl = `https://mj-comfy-ui.oss-cn-shenzhen.aliyuncs.com/${result.name}`
          console.log('生成的图片URL:', imageUrl)

          if (type === 'start') {
            this.startImage = imageUrl
          } else {
            this.endImage = imageUrl
          }
          Message.success(`${label}图片上传成功!`)
        } else {
          Message.error(`${label}图片上传失败，请重试!`)
        }
      } catch (error) {
        console.error('上传图片失败:', error)
        const label = type === 'start' ? '首帧' : '尾帧'
        Message.error(`${label}图片上传失败，请检查网络!`)
      }
    },

    // 移除首帧图片
    removeStartImage() {
      this.startImage = ''
    },

    // 移除尾帧图片
    removeEndImage() {
      this.endImage = ''
    },

    // 拖拽上传相关方法
    handleDragOver(event, type) {
      this.isDragging = type
    },
    handleDragLeave(type) {
      if (this.isDragging === type) {
        this.isDragging = null
      }
    },
    handleDrop(event, type) {
      this.isDragging = null
      const files = event.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        if (file.type.startsWith('image/')) {
          this.processImage(file, type)
        } else {
          Message.warning('请拖入图片文件!')
        }
      }
    },

    // 回车发送（Shift+Enter换行）
    handleTextareaKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.startTask()
      }
    },

    // 选择风格
    selectStyle(style) {
      this.selectedStyle = style
      // 根据风格自动填充提示词
      if (style === '刷划类') {
        this.textarea = '甲油胶料体呈现柔和的****色调（是否带细闪），甲油料体被扁平刷头按压在表面后向右划出S形波浪弧线（或向某个方向划出某种线）, 刷痕边缘因胶体堆积形成明显隆起的高光轴线, 稠密质地使胶体产生缓慢回流与流平效应, 特写, 物理真实渲染'
      } else if (style === '滴落类') {
        this.textarea = '甲油胶料体呈现****质地（是否带有细闪）,高粘度甲油料体从垂直刷头尖端凝聚成饱满胶珠, 表面张力使胶珠呈球形悬垂, 滴落过程中（是否细闪流淌），粘稠质地使胶体拉伸形成丝状尾流，滴入下方的产品玻璃瓶中，特写，物理真实渲染'
      }
    },

    // 切换时长菜单
    toggleDurationMenu() {
      this.showDurationMenu = !this.showDurationMenu
      this.showFrameMenu = false
      this.showResolutionMenu = false
    },

    // 选择视频时长
    selectDuration(duration) {
      this.selectedDuration = duration
      this.showDurationMenu = false
    },

    // 切换帧数控制菜单
    toggleFrameMenu() {
      this.showFrameMenu = !this.showFrameMenu
      this.showDurationMenu = false
      this.showResolutionMenu = false
    },

    // 选择帧数控制方式
    selectFrameMode(mode) {
      if (mode === '智能多帧') {
        Message.info('智能多帧功能正在开发中，敬请期待！')
        this.showFrameMenu = false
        return
      }
      this.selectedFrameMode = mode
      // 切换到首帧模式时，清空尾帧图片
      if (mode === '首帧') {
        this.endImage = ''
      }
      this.showFrameMenu = false
    },

    // 切换画面大小菜单
    toggleResolutionMenu() {
      this.showResolutionMenu = !this.showResolutionMenu
      this.showDurationMenu = false
      this.showFrameMenu = false
    },

    // 选择画面大小
    selectResolution(resolution) {
      this.selectedResolution = resolution
      this.showResolutionMenu = false
    },

    // 替换首尾帧工作流中的动态变量
    replaceWorkflowVariables(workflow, seed, startImageUrl, endImageUrl, prompt, durationSeconds, resolution, selectedStyle) {
      const workflowCopy = JSON.parse(JSON.stringify(workflow))
      console.log('🔍 开始替换工作流变量...')
      console.log('参数:', { seed, startImageUrl, endImageUrl, prompt, durationSeconds, durationSecondsType: typeof durationSeconds, resolution, resolutionType: typeof resolution })

      // 生成动态文件名
      const timestamp = Date.now()
      const randomNum = Math.floor(Math.random() * 99999)
      const outputFilename = `tmp-comfyui/video/${timestamp}_${randomNum}.mp4`
      console.log('🎥 生成的视频文件名:', outputFilename)

      // 遍历所有节点替换变量
      for (const nodeId in workflowCopy) {
        const node = workflowCopy[nodeId]
        if (node.inputs) {
          for (const inputKey in node.inputs) {
            const inputValue = node.inputs[inputKey]
            if (typeof inputValue === 'string') {
              if (inputValue.includes('{{SEED}}')) {
                node.inputs[inputKey] = String(seed)
                console.log(`✅ 节点${nodeId}.${inputKey}: {{SEED}} -> ${seed}`)
              } else if (inputValue.includes('{{START_IMAGE_URL}}')) {
                node.inputs[inputKey] = startImageUrl
                console.log(`✅ 节点${nodeId}.${inputKey}: {{START_IMAGE_URL}} -> ${startImageUrl}`)
              } else if (inputValue.includes('{{END_IMAGE_URL}}')) {
                node.inputs[inputKey] = endImageUrl
                console.log(`✅ 节点${nodeId}.${inputKey}: {{END_IMAGE_URL}} -> ${endImageUrl}`)
              } else if (inputValue.includes('{{PROMPT}}')) {
                node.inputs[inputKey] = prompt
                console.log(`✅ 节点${nodeId}.${inputKey}: {{PROMPT}} -> ${prompt}`)
              } else if (inputValue.includes('{{DURATION_SECONDS}}')) {
                // 注意：这里替换为整数而不是字符串
                node.inputs[inputKey] = durationSeconds
                console.log(`✅ 节点${nodeId}.${inputKey}: {{DURATION_SECONDS}} -> ${durationSeconds} (类型: ${typeof durationSeconds})`)
              } else if (inputValue.includes('{{RESOLUTION}}')) {
                // 替换画面大小，转换为整数
                node.inputs[inputKey] = parseInt(resolution, 10)
                console.log(`✅ 节点${nodeId}.${inputKey}: {{RESOLUTION}} -> ${resolution} (类型: ${typeof parseInt(resolution, 10)})`)
              } else if (inputValue.includes('{{OUTPUT_FILENAME}}')) {
                // 保持JSON数组字符串格式，只替换模板变量部分
                node.inputs[inputKey] = inputValue.replace('{{OUTPUT_FILENAME}}', outputFilename)
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OUTPUT_FILENAME}} -> ${node.inputs[inputKey]}`)
              }
            } else if (typeof inputValue === 'number') {
              // 处理数字类型的字段
              if (nodeId === '54' && inputKey === 'value') {
                // 时长字段
                node.inputs[inputKey] = durationSeconds
                console.log(`✅ 节点${nodeId}.${inputKey}: 直接替换为 ${durationSeconds}`)
              } else if (nodeId === '51' && inputKey === 'seed') {
                // 种子字段 - 替换为前端生成的随机种子
                node.inputs[inputKey] = seed
                console.log(`✅ 节点${nodeId}.${inputKey}: 替换为前端生成的种子 ${seed}`)
              } else if (nodeId === '50' && inputKey === 'scale_to_length') {
                // 画面大小字段
                node.inputs[inputKey] = parseInt(resolution, 10)
                console.log(`✅ 节点${nodeId}.${inputKey}: 直接替换为 ${resolution}`)
              }
            }
          }
        }
      }

      console.log('🔍 替换后的54号节点:', JSON.stringify(workflowCopy['54'], null, 2))
      console.log('🔍 替换后的50号节点:', JSON.stringify(workflowCopy['50'], null, 2))
      console.log('🔍 替换后的51号节点:', JSON.stringify(workflowCopy['51'], null, 2))

      // 根据风格选择修改工作流参数
      if (selectedStyle === '常规类') {
        // 常规类：关闭滴画LoRA
        workflowCopy['78'].inputs.strength = 0
        workflowCopy['79'].inputs.strength = 0
        console.log('✅ 常规类：节点78/79 strength 设为0')
      } else if (selectedStyle === '刷划类') {
        // 刷划类：保留滴画LoRA参数，修改提示词前缀
        workflowCopy['34'].inputs.prompt = `Jiauliotie, 刷划动态, ${prompt}`
        console.log('✅ 刷划类：提示词前缀添加 Jiauliotie, 刷划动态')
      } else if (selectedStyle === '滴落类') {
        // 滴落类：保留滴画LoRA参数，修改提示词前缀
        workflowCopy['34'].inputs.prompt = `Jiauliotie, 垂滴动态, ${prompt}`
        console.log('✅ 滴落类：提示词前缀添加 Jiauliotie, 垂滴动态')
      }

      return workflowCopy
    },

    // 替换首帧工作流中的动态变量(仅需要首帧图片)
    replaceSingleFrameWorkflowVariables(workflow, seed, startImageUrl, prompt, durationSeconds, resolution, selectedStyle) {
      const workflowCopy = JSON.parse(JSON.stringify(workflow))
      console.log('🔍 开始替换首帧工作流变量...')
      console.log('参数:', { seed, startImageUrl, prompt, durationSeconds, durationSecondsType: typeof durationSeconds, resolution, resolutionType: typeof resolution })

      // 生成动态文件名
      const timestamp = Date.now()
      const randomNum = Math.floor(Math.random() * 99999)
      const outputFilename = `tmp-comfyui/video/${timestamp}_${randomNum}.mp4`
      console.log('🎥 生成的视频文件名:', outputFilename)

      // 遍历所有节点替换变量
      for (const nodeId in workflowCopy) {
        const node = workflowCopy[nodeId]
        if (node.inputs) {
          for (const inputKey in node.inputs) {
            const inputValue = node.inputs[inputKey]
            if (typeof inputValue === 'string') {
              if (inputValue.includes('{{SEED}}')) {
                node.inputs[inputKey] = String(seed)
                console.log(`✅ 节点${nodeId}.${inputKey}: {{SEED}} -> ${seed}`)
              } else if (inputValue.includes('{{START_IMAGE_URL}}')) {
                node.inputs[inputKey] = startImageUrl
                console.log(`✅ 节点${nodeId}.${inputKey}: {{START_IMAGE_URL}} -> ${startImageUrl}`)
              } else if (inputValue.includes('{{PROMPT}}')) {
                node.inputs[inputKey] = prompt
                console.log(`✅ 节点${nodeId}.${inputKey}: {{PROMPT}} -> ${prompt}`)
              } else if (inputValue.includes('{{DURATION_SECONDS}}')) {
                node.inputs[inputKey] = durationSeconds
                console.log(`✅ 节点${nodeId}.${inputKey}: {{DURATION_SECONDS}} -> ${durationSeconds} (类型: ${typeof durationSeconds})`)
              } else if (inputValue.includes('{{RESOLUTION}}')) {
                node.inputs[inputKey] = parseInt(resolution, 10)
                console.log(`✅ 节点${nodeId}.${inputKey}: {{RESOLUTION}} -> ${resolution} (类型: ${typeof parseInt(resolution, 10)})`)
              } else if (inputValue.includes('{{OUTPUT_FILENAME}}')) {
                node.inputs[inputKey] = inputValue.replace('{{OUTPUT_FILENAME}}', outputFilename)
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OUTPUT_FILENAME}} -> ${node.inputs[inputKey]}`)
              }
            } else if (typeof inputValue === 'number') {
              // 处理数字类型的字段
              if (nodeId === '133' && inputKey === 'Value') {
                // 时长字段
                node.inputs[inputKey] = durationSeconds
                console.log(`✅ 节点${nodeId}.${inputKey}: 直接替换为 ${durationSeconds}`)
              } else if ((nodeId === '27' || nodeId === '90') && inputKey === 'seed') {
                // 种子字段 - 替换为前端生成的随机种子
                node.inputs[inputKey] = seed
                console.log(`✅ 节点${nodeId}.${inputKey}: 替换为前端生成的种子 ${seed}`)
              } else if (nodeId === '102' && inputKey === 'value') {
                // 画面大小字段
                node.inputs[inputKey] = parseInt(resolution, 10)
                console.log(`✅ 节点${nodeId}.${inputKey}: 直接替换为 ${resolution}`)
              }
            }
          }
        }
      }

      console.log('🔍 替换后的133号节点(时长):', JSON.stringify(workflowCopy['133'], null, 2))
      console.log('🔍 替换后的102号节点(画面大小):', JSON.stringify(workflowCopy['102'], null, 2))
      console.log('🔍 替换后的27号节点(种子):', JSON.stringify(workflowCopy['27'], null, 2))
      console.log('🔍 替换后的90号节点(种子):', JSON.stringify(workflowCopy['90'], null, 2))
      console.log('🔍 替换后的145号节点(首帧):', JSON.stringify(workflowCopy['145'], null, 2))
      console.log('🔍 替换后的142号节点(提示词):', JSON.stringify(workflowCopy['142'], null, 2))
      console.log('🔍 替换后的146号节点(视频输出):', JSON.stringify(workflowCopy['146'], null, 2))

      // 根据风格选择修改工作流参数
      if (selectedStyle === '常规类') {
        // 常规类：关闭滴画LoRA
        workflowCopy['152'].inputs.strength = 0
        workflowCopy['153'].inputs.strength = 0
        console.log('✅ 常规类：节点152/153 strength 设为0')
      } else if (selectedStyle === '刷划类') {
        // 刷划类：保留滴画LoRA参数，修改提示词前缀
        workflowCopy['142'].inputs.prompt = `Jiauliotie, 刷划动态, ${prompt}`
        console.log('✅ 刷划类：提示词前缀添加 Jiauliotie, 刷划动态')
      } else if (selectedStyle === '滴落类') {
        // 滴落类：保留滴画LoRA参数，修改提示词前缀
        workflowCopy['142'].inputs.prompt = `Jiauliotie, 垂滴动态, ${prompt}`
        console.log('✅ 滴落类：提示词前缀添加 Jiauliotie, 垂滴动态')
      }

      return workflowCopy
    },
    closeAllMenus() {
      this.showDurationMenu = false
      this.showFrameMenu = false
      this.showResolutionMenu = false
    },

    // 处理 textarea 获得焦点
    handleTextareaFocus() {
      // 确保导航框处于放大状态
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }
    },
    // 处理 textarea 输入
    handleTextareaInput() {
      // 用户输入时保持放大状态
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }
    },
    // 处理 textarea 失去焦点
    handleTextareaBlur() {
      // 不做任何处理，保持当前状态
    },
    // 处理 textarea 点击
    handleTextareaClick() {
      // 点击输入框时放大
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }
    },
    // 导航栏状态
    handleScroll(val) {
      if (val) {
        // 放大状态
        if (this.$refs.navigation_items) {
          this.$refs.navigation_items.style.width = '70%'
          // 高度保持不变，不再动态修改
        }
        if (this.$refs.ai_assistant) this.$refs.ai_assistant.style.opacity = 1
        if (this.$refs.scroll_bottom) this.$refs.scroll_bottom.style.opacity = 1
        this.scrollStatus = true
      } else {
        // 缩小状态
        if (this.$refs.navigation_items) {
          this.$refs.navigation_items.style.width = '60%'
          // 高度保持不变，不再动态修改
        }
        if (this.$refs.ai_assistant) this.$refs.ai_assistant.style.opacity = 0
        if (this.$refs.scroll_bottom) this.$refs.scroll_bottom.style.opacity = 0
        this.scrollStatus = false
      }
    },
    // 回滚到底部
    scrollToBottom() {
      if (this.scrollStatus) {
        this.$emit('scrollToBottom')
      }
    },

    // Ctrl+V 粘贴图片（暂时禁用，因为需要区分首帧/尾帧）
    async handleKeyPaste(event) {
      // 粘贴功能暂时禁用，需要用户明确选择首帧或尾帧
      // if (event.ctrlKey && event.key === 'v') {
      //   try {
      //     const items = (event.clipboardData || window.clipboardData).items
      //     for (let i = 0; i < items.length; i++) {
      //       if (items[i].type.indexOf('image') !== -1) {
      //         const blob = items[i].getAsFile()
      //         await this.processImage(blob, 'start') // 默认粘贴到首帧
      //         break
      //       }
      //     }
      //   } catch (error) {
      //     console.error('粘贴图片失败:', error)
      //   }
      // }
    },

    // 处理来自父组件的重新生成请求
    handleRegenerateFromParent(regenerateData) {
      if (!regenerateData) return
      console.log('接收到重新生成请求:', regenerateData)
      // 填充输入框和图片
      this.textarea = regenerateData.content
      this.startImage = regenerateData.startImage
      this.endImage = regenerateData.endImage
      this.selectedDuration = regenerateData.duration
      this.selectedFrameMode = regenerateData.frameMode
      this.selectedResolution = regenerateData.resolution
      // 清除 sessionStorage
      sessionStorage.removeItem('video_regenerate')
      // 自动触发生成
      this.$nextTick(() => {
        this.startTask()
      })
    },

    // 处理来自父组件的重新填入请求（仅回填，不自动生成）
    handleRefillFromParent(refillData) {
      if (!refillData) return
      console.log('接收到重新填入请求:', refillData)
      this.textarea = refillData.content
      this.startImage = refillData.startImage
      this.endImage = refillData.endImage || ''
      this.selectedDuration = refillData.duration || '3s'
      this.selectedFrameMode = refillData.frameMode || '首尾帧'
      this.selectedResolution = refillData.resolution || '720'
      // 确保导航框放大显示
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }
    },

    // 开始任务
    async startTask() {
      if (!this.canSend || this.processing) {
        return
      }

      // 确保放大状态
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }

      if (!this.startImage) {
        Message.warning('请先上传首帧图片!')
        return
      }

      // 只有首尾帧模式才需要尾帧
      if (this.selectedFrameMode === '首尾帧' && !this.endImage) {
        Message.warning('请先上传尾帧图片!')
        return
      }

      if (this.textarea.trim() === '') {
        Message.warning('请输入视频描述!')
        return
      }

      const userMessage = this.textarea
      const userStartImage = this.startImage
      const userEndImage = this.endImage
      const userDuration = this.selectedDuration
      const userResolution = this.selectedResolution
      const userFrameMode = this.selectedFrameMode

      // 清空输入
      this.textarea = ''
      this.startImage = ''
      if (userFrameMode === '首尾帧') {
        this.endImage = ''
      }

      // 添加用户消息到历史
      this.videoChatHistory.push({
        role: 'user',
        content: userMessage,
        startImage: userStartImage,
        endImage: userEndImage,
        duration: userDuration,
        frameMode: this.selectedFrameMode,
        resolution: userResolution,
        timestamp: new Date().toISOString()
      })

      // 更新界面
      this.$emit('chatUpdate', this.videoChatHistory)
      this.$emit('aiTyping', true)

      this.processing = true

      // 清空 textarea 后立即强制保持放大状态
      this.$nextTick(() => {
        this.handleScroll(true)
      })

      try {
        // 生成随机种子
        const seed = Math.floor(Math.random() * 100000000000000)
        console.log('🎲 生成的种子:', seed)

        // 解析时长（从 "3s" 提取数字 3）
        const durationSeconds = parseInt(userDuration.replace('s', ''), 10)
        console.log('🕹️ 解析的时长:', durationSeconds, '类型:', typeof durationSeconds)

        // 解析画面大小（从 "720" 提取数字 720）
        const resolution = parseInt(userResolution, 10)
        console.log('📺 解析的画面大小:', resolution, '类型:', typeof resolution)

        // 确保时长是有效数字
        if (isNaN(durationSeconds) || durationSeconds <= 0) {
          throw new Error(`无效的时长值: ${userDuration}`)
        }

        // 确保画面大小是有效数字
        if (isNaN(resolution) || resolution <= 0) {
          throw new Error(`无效的画面大小值: ${userResolution}`)
        }

        console.log('🖼️ 首帧图片URL:', userStartImage)
        if (userFrameMode === '首尾帧') {
          console.log('🖼️ 尾帧图片URL:', userEndImage)
        }
        console.log('📝 提示词:', userMessage)
        console.log('🎬 帧数控制模式:', userFrameMode)

        let promptData
        // 根据帧数控制模式选择不同的工作流
        if (userFrameMode === '首尾帧') {
          // 记录当前使用的工作流类型
          this.currentWorkflowType = '首尾帧'
          // 替换首尾帧工作流变量
          promptData = this.replaceWorkflowVariables(
            videoWorkflow,
            seed,
            userStartImage,
            userEndImage,
            userMessage,
            durationSeconds,
            resolution,
            this.selectedStyle
          )
          console.log('👍 首尾帧工作流替换完成')

          // 打印关键节点，确认变量替换正确
          console.log('🔍 76号节点(首帧):', JSON.stringify(promptData['76'], null, 2))
          console.log('🔍 77号节点(尾帧):', JSON.stringify(promptData['77'], null, 2))
          console.log('🔍 34号节点(提示词):', JSON.stringify(promptData['34'], null, 2))
          console.log('🔍 54号节点(时长):', JSON.stringify(promptData['54'], null, 2))
          console.log('🔍 50号节点(画面大小):', JSON.stringify(promptData['50'], null, 2))
          console.log('🔍 75号节点(视频输出):', JSON.stringify(promptData['75'], null, 2))
        } else if (userFrameMode === '首帧') {
          // 记录当前使用的工作流类型
          this.currentWorkflowType = '首帧'
          // 替换首帧工作流变量
          promptData = this.replaceSingleFrameWorkflowVariables(
            singleFrameWorkflow,
            seed,
            userStartImage,
            userMessage,
            durationSeconds,
            resolution,
            this.selectedStyle
          )
          console.log('👍 首帧工作流替换完成')

          // 打印关键节点，确认变量替换正确
          console.log('🔍 145号节点(首帧):', JSON.stringify(promptData['145'], null, 2))
          console.log('🔍 142号节点(提示词):', JSON.stringify(promptData['142'], null, 2))
          console.log('🔍 133号节点(时长):', JSON.stringify(promptData['133'], null, 2))
          console.log('🔍 99号节点(画面大小):', JSON.stringify(promptData['99'], null, 2))
          console.log('🔍 146号节点(视频输出):', JSON.stringify(promptData['146'], null, 2))
        } else {
          throw new Error(`不支持的帧数控制模式: ${userFrameMode}`)
        }

        this.clientId = uuidv4()
        const data = {
          prompt: promptData,
          client_id: this.clientId
        }

        console.log('🚀 准备发送视频生成任务...')
        console.log('📦 客户端ID:', this.clientId)
        console.log('📦 工作流节点数量:', Object.keys(promptData).length)
        console.log('📦 完整工作流JSON:', JSON.stringify(promptData, null, 2))

        // 发送 Prompt 请求
        const result = await genPrompt(data)

        console.log('📨 genPrompt 返回结果:', result)

        if (result && result.prompt_id) {
          console.log('✅ 任务提交成功，promptId:', result.prompt_id)
          // 监听 WebSocket 进度
          await this.listenToVideoProgress(result.prompt_id, userMessage, userStartImage, userEndImage)
        } else {
          throw new Error('任务提交失败')
        }
      } catch (error) {
        console.error('❗ 生成视频失败:', error)
        let errorMessage = '视频生成失败，请稍后再试!'
        if (error.response) {
          console.error('⚠️ 服务器返回错误:', error.response.status)
          console.error('错误详情:', error.response.data)
          errorMessage = `服务器错误: ${error.response.status}`
        } else if (error.message) {
          errorMessage = error.message
        }
        // 添加失败的 AI 回复到历史
        this.videoChatHistory.push({
          role: 'assistant',
          content: '抱歉，似乎发生了某些问题',
          timestamp: new Date().toISOString(),
          isError: true // 标记为错误消息
        })
        // 发送对话历史给父组件
        this.$emit('chatUpdate', this.videoChatHistory)
        this.$emit('aiTyping', false)
        Message.error(errorMessage)
        this.processing = false
      }
    },
    // 监听视频生成进度
    async listenToVideoProgress(promptId, taskPrompt, taskStartImage, taskEndImage) {
      // 重置标志位
      this.videoFetched = false
      this.ws = new WebSocket(`${getLastUsedBaseUrl()}/ws?clientId=${this.clientId}`)
      const wsTimeout = setTimeout(() => {
        Message.warning('网络连接超时,请重新尝试!')
        this.ws.close()
      }, 30000)
      this.ws.onopen = () => {
        console.log('✅ WebSocket 连接成功')
        // 记录任务开始
        getTaskStart({
          type: VIDEO_GENERATION,
          prompt: taskPrompt || '',
          input_images: [taskStartImage, taskEndImage].filter(Boolean)
        }).then(rep => {
          this.taskLogId = rep.data.task.id
        })
        clearTimeout(wsTimeout)
      }
      this.ws.onmessage = async (event) => {
        clearTimeout(wsTimeout)
        const data = event.data
        if (typeof data === 'string') {
          try {
            const msg = JSON.parse(data)
            // 过滤掉监控消息，只显示关键信息
            if (msg.type !== 'crystools.monitor') {
              console.log('📡 收到消息:', msg)
            }

            if (msg.type === 'executing') {
              const execData = msg.data
              if (execData.node === null && execData.prompt_id === promptId) {
                console.log('✅ 视频生成完成')
                this.currentExecutingNode = ''
                this.$emit('nodeUpdate', '')
                this.ws.close()
                // 防止重复调用
                if (!this.videoFetched) {
                  this.videoFetched = true
                  await this.fetchVideoUrl(promptId)
                }
              } else if (execData.node !== null) {
                console.log(`🔄 正在执行节点: ${execData.node}`)
                // 更新当前执行的节点名称
                const nodeName = this.nodeNameMap[execData.node] || `执行节点 ${execData.node}`
                this.currentExecutingNode = nodeName
                this.$emit('nodeUpdate', nodeName)
              }
            } else if (msg.type === 'progress') {
              const value = msg.data.value
              const max = msg.data.max
              const current = (value / max * 100).toFixed(2)
              console.log(`📈 进度: ${current}%`)
              // 发送进度给父组件
              this.$emit('progressUpdate', current)
            } else if (msg.type === 'status') {
              console.log('📊 状态更新:', msg.data)
              // 如果队列为空且没有执行中的任务，说明任务已经完成或跳过
              if (msg.data.status && msg.data.status.exec_info) {
                const execInfo = msg.data.status.exec_info
                if (execInfo.queue_remaining === 0) {
                  console.log('🤔 队列为空，检查任务状态...')
                  // 等待2秒后检查任务状态
                  setTimeout(async () => {
                    await this.checkTaskStatus(promptId)
                  }, 2000)
                }
              }
            } else if (msg.type === 'execution_error') {
              console.error('❌ 执行错误:', msg.data)
              // 添加失败的 AI 回复到历史
              this.videoChatHistory.push({
                role: 'assistant',
                content: '抱歉，似乎发生了某些问题',
                timestamp: new Date().toISOString(),
                isError: true // 标记为错误消息
              })
              // 发送对话历史给父组件
              this.$emit('chatUpdate', this.videoChatHistory)
              Message.error('视频生成过程中出现错误，请查看控制台')
              this.ws.close()
              this.processing = false
              this.$emit('aiTyping', false)
            }
          } catch (error) {
            console.error('WebSocket消息解析失败:', error)
          }
        }
      }
      this.ws.onclose = async () => {
        console.log('🔌 WebSocket 连接关闭')
        try {
          const index = await cudaIndexNum()
          if (index) {
            await cuda.changeCudaCurrentStatus(index, 'leisure')
          }
        } catch (error) {
          console.log(error)
        }
      }
      this.ws.onerror = (error) => {
        console.error('❌ WebSocket 错误:', error)
        Message.error('WebSocket 连接错误')
        this.processing = false
        this.$emit('aiTyping', false)
      }
    },
    // 检查任务状态
    async checkTaskStatus(promptId) {
      try {
        console.log('🔍 检查任务状态:', promptId)
        const historyRequest = axios.create({
          baseURL: getLastUsedBaseUrl(),
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const response = await historyRequest.get(`/history/${promptId}`)
        const promptInfo = response.data[promptId]
        console.log('📝 任务历史信息:', promptInfo)
        console.log('📝 任务历史完整响应:', JSON.stringify(response.data, null, 2))

        if (!promptInfo) {
          console.error('❌ 找不到任务信息，任务可能还在排队中...')
          // 任务可能还在排队，等待更长时间
          return
        }

        // 检查任务状态
        if (promptInfo.status) {
          console.log('📊 任务状态:', promptInfo.status.status_str)
          console.log('📊 完整状态信息:', JSON.stringify(promptInfo.status, null, 2))

          // 检查是否有输出
          if (promptInfo.outputs) {
            console.log('📊 任务输出:', JSON.stringify(promptInfo.outputs, null, 2))
          } else {
            console.warn('⚠️ 任务没有输出，可能没有真正执行')
          }

          if (promptInfo.status.status_str === 'success') {
            // 检查是否真的有输出
            if (promptInfo.outputs && Object.keys(promptInfo.outputs).length > 0) {
              console.log('✅ 任务已完成且有输出，获取视频URL')
              // 防止重复调用
              if (!this.videoFetched) {
                this.videoFetched = true
                await this.fetchVideoUrl(promptId)
              } else {
                console.log('⚠️ 视频已获取，跳过重复调用')
              }
            } else {
              console.error('❌ 任务状态为success但没有输出，工作流可能没有真正执行')
              Message.error('工作流执行异常，请检查ComfyUI服务器日志')
              this.processing = false
              this.$emit('aiTyping', false)
              this.ws.close()
            }
          } else if (promptInfo.status.status_str === 'error') {
            console.error('❌ 任务执行失败:', promptInfo.status)
            if (promptInfo.status.messages && promptInfo.status.messages.length > 0) {
              console.error('错误信息:', promptInfo.status.messages)
              Message.error(`任务执行失败: ${JSON.stringify(promptInfo.status.messages)}`)
            } else {
              Message.error('任务执行失败，请检查工作流配置')
            }
            this.processing = false
            this.$emit('aiTyping', false)
            this.ws.close()
          } else {
            console.log('🔄 任务状态:', promptInfo.status.status_str)
          }
        } else {
          console.error('❌ 任务状态信息缺失')
        }
      } catch (error) {
        console.error('🚫 检查任务状态失败:', error)
      }
    },
    // 记录任务结束
    async recordTaskEnd(videoUrl) {
      try {
        const imgInfo = await getImageSizeByLoading(videoUrl)
        const info = {
          details: [{ path: videoUrl, imgInfo: imgInfo }]
        }
        await getTaskEnd({ result: JSON.stringify(info) }, this.taskLogId)
      } catch (error) {
        console.error('记录任务结束失败:', error)
      }
    },
    // 获取视频URL
    async fetchVideoUrl(promptId) {
      try {
        const historyRequest = axios.create({
          baseURL: getLastUsedBaseUrl(),
          timeout: 5000,
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const response = await historyRequest.get(`/history/${promptId}`)
        const promptInfo = response.data[promptId]
        console.log('📄 完整的promptInfo:', promptInfo)
        if (promptInfo.status.status_str === 'success') {
          // 根据工作流类型选择不同的输出节点
          // 首尾帧工作流使用75号节点，首帧工作流使用146号节点
          const nodeId = this.currentWorkflowType === '首帧' ? '146' : '75'
          console.log('🔍 当前工作流类型:', this.currentWorkflowType)
          console.log('🔍 使用的输出节点ID:', nodeId)
          if (!promptInfo.outputs || !promptInfo.outputs[nodeId]) {
            console.error(`输出结构异常，无法找到节点${nodeId}的输出数据`)
            Message.error('获取生成结果失败：输出结构异常')
            this.processing = false
            this.$emit('aiTyping', false)
            return
          }
          // 从 outputs获取oss_path（VideoCombineOSS节点输出格式为 gifs 数组）
          const nodeOutput = promptInfo.outputs[nodeId]
          console.log(`📊 ${nodeId}号节点输出:`, nodeOutput)
          let ossPath = null
          // 检查gifs数组中的oss_path
          if (nodeOutput.gifs && nodeOutput.gifs.length > 0 && nodeOutput.gifs[0].oss_path) {
            ossPath = nodeOutput.gifs[0].oss_path
          }
          if (!ossPath) {
            console.error('无法从输出中获取oss_path')
            Message.error('获取视频路径失败')
            this.processing = false
            this.$emit('aiTyping', false)
            return
          }
          console.log('🎬 获取的OSS路径:', ossPath)
          // 生成完整OSS URL
          const videoUrl = genOssImage(ossPath)
          console.log('✅ 生成的视频URL:', videoUrl)
          // 添加AI回复到历史
          this.videoChatHistory.push({
            role: 'assistant',
            content: '视频生成成功！',
            video: videoUrl,
            timestamp: new Date().toISOString()
          })
          this.$emit('chatUpdate', this.videoChatHistory)
          this.$emit('aiTyping', false)
          this.processing = false
          // 记录任务结束
          await this.recordTaskEnd(videoUrl)
          Message.success('视频生成成功!')
        } else {
          console.error('任务状态异常:', promptInfo.status)
          Message.error(`任务执行失败: ${promptInfo.status.status_str}`)
          this.processing = false
          this.$emit('aiTyping', false)
        }
      } catch (error) {
        console.error('获取视频文件名失败：', error)
        console.error('错误详情:', error.response?.data || error.message)
        Message.error('获取生成结果失败，请查看控制台')
        this.processing = false
        this.$emit('aiTyping', false)
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import "@/style/common.less";
@import "@/assets/fonts/style.css";
@import "@/assets/font_sczlgzino/iconfont.css";

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-family: "icomoon";
  overflow: hidden;
}

.iconfont {
  font-family: "iconfont" !important;
  font-size: 25px;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.active {
  background: @basicselect;
}

.navigation {
  position: absolute;
  width: calc(100vw - 80px);
  bottom: 15px;
  left: 100px;
  z-index: 8;
  transition: all 0.5s;

  .navigation_bottom {
    position: relative;
    width: 1536px;
    height: 50px;
    font-size: 23px;
    margin: 0 auto;
    justify-content: space-between;
    margin-bottom: 10px;

    .ai_assistant {
      position: absolute;
      bottom: -10px;
      width: 120px;
      height: 120px;
      opacity: 0;
      transition: all 0.5s;
    }

    .scroll_bottom {
      position: absolute;
      right: 0px;
      font-size: 20px;
      color: @font_white;
      background-color: #c1c1c1;
      padding: 10px 15px;
      cursor: pointer;
      border-radius: @border_radius_3;
      transition: all 0.5s;
      opacity: 0;
    }
  }

  .navigation_items {
    position: relative;
    width: 1336px;
    height: 220px;
    display: flex;
    flex-direction: column;
    background: rgba(235, 235, 235, 0.5);
    backdrop-filter: blur(10px);
    border: 1px solid @font_white;
    border-radius: @border_radius;
    transition: all 0.3s;
    margin: 0 auto;
    box-shadow: 0 0 10px 0px rgba(0, 0, 0, 0.2);

    .main_input {
      width: 100%;
      height: 220px;
      display: flex;
      flex-direction: column;
      padding: 15px 20px;
      gap: 10px;

      // 上传框行
      .upload-row {
        display: flex;
        gap: 10px;
        flex-shrink: 0;
      }

      // 上传区域
      .upload-section {
        position: relative;
        width: 70px;
        height: 70px;
        border: 2px dashed #d0d0d0;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;
        flex-shrink: 0;
        overflow: hidden;
        background: white;

        &:hover {
          border-color: @morejoy;
          background: #fff8f3;
        }

        &.drag-over {
          border-color: @morejoy;
          background: #fff3e8;
          box-shadow: 0 0 12px rgba(255, 140, 66, 0.3);
          transform: scale(1.02);
        }

        .upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          width: 100%;
          height: 100%;

          .upload-plus {
            font-size: 28px;
            color: #999;
            font-weight: 300;
            line-height: 1;
          }

          .upload-label {
            font-size: 11px;
            color: #666;
            font-weight: 400;
          }
        }

        .uploaded-preview {
          position: relative;
          width: 100%;
          height: 100%;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .remove-btn {
            position: absolute;
            top: 3px;
            right: 3px;
            width: 18px;
            height: 18px;
            background: rgba(0, 0, 0, 0.6);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
              background: rgba(255, 0, 0, 0.8);
              transform: scale(1.1);
            }
          }
        }
      }

      // 视频参数选择区域
      .video-options {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;

        // 风格选择按钮组
        .style-options {
          display: flex;
          gap: 0;
          flex-shrink: 0;

          .style-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 8px 12px;
            min-height: 36px;
            background: white;
            border: 1px solid #d0d0d0;
            border-radius: 8px;
            font-size: 12px;
            color: #999;
            font-weight: 400;
            cursor: pointer;
            transition: all 0.3s;
            user-select: none;
            position: relative;
            z-index: 0;

            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: transparent;
              border-radius: inherit;
              transition: all 0.3s;
              z-index: -1;
            }

            &:not(:first-child) {
              margin-left: 0;
              border-left: none;
            }

            &:not(:last-child) {
              border-right: none;
            }

            &:first-child {
              border-radius: 8px 0 0 8px;
            }

            &:last-child {
              border-radius: 0 8px 8px 0;
            }

            &:not(:first-child):not(:last-child) {
              border-radius: 0;
            }

            &:hover {
              color: @morejoy;
            }

            &.active {
              color: white;
              font-weight: 500;
              z-index: 1;

              &::before {
                top: 3px;
                left: 3px;
                right: 3px;
                bottom: 3px;
                background: @morejoy;
                border-radius: 5px;
              }
            }
          }
        }

        .option-dropdown {
          position: relative;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 12px;
          background: white;
          border: 1px solid #d0d0d0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
          user-select: none;

          &:hover {
            border-color: @morejoy;
            background: #fff8f3;
          }

          .option-label {
            font-size: 12px;
            color: #999;
            font-weight: 400;
          }

          .option-value {
            font-size: 13px;
            color: #333;
            font-weight: 500;
          }

          .arrow {
            font-size: 12px;
            color: #999;
            transition: transform 0.3s;
          }

          .dropdown-menu {
            position: absolute;
            bottom: calc(100% + 5px);
            left: 0;
            min-width: 100%;
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 100;
            overflow: hidden;

            .menu-item {
              padding: 10px 15px;
              font-size: 13px;
              color: #333;
              cursor: pointer;
              transition: all 0.2s;

              &:hover {
                background: #fff8f3;
                color: @morejoy;
              }

              &.active {
                background: #fff8f3;
                color: @morejoy;
                font-weight: 500;

                &::after {
                  content: '✓';
                  float: right;
                  font-weight: bold;
                }
              }
            }
          }
        }
      }

      // 输入框区域
      .input-area {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 15px;
        position: relative;
        min-height: 60px;

        textarea {
          flex: 1;
          height: 100%;
          min-height: 60px;
          font-family: '微软雅黑';
          font-size: 18px;
          padding: 10px 20px;
          border: none;
          background: none;
          resize: none;
          outline: none;
        }

        .sent {
          position: absolute;
          right: 0;
          width: 60px;
          height: 60px;
          text-align: center;
          line-height: 60px;
          font-size: 35px;
          color: @font_white;
          border: none;
          border-radius: 30px;
          background-color: @font_gray;
          cursor: pointer;
          opacity: 0.7;
          transition: all 0.3s;

          &.has_input {
            background-color: @morejoy;
            opacity: 1;
          }

          &:hover:not(:disabled) {
            opacity: 1;
            font-size: 38px;
          }

          &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }

          .rotating {
            animation: rotate 1s linear infinite;
          }
        }
      }
    }

    /* 放大后的发送按钮（绝对定位） */
    .sent_expanded {
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);
      width: 60px;
      height: 60px;
      text-align: center;
      line-height: 60px;
      font-size: 35px;
      color: @font_white;
      border: none;
      border-radius: 30px;
      background-color: @font_gray;
      cursor: pointer;
      opacity: 0.7;
      transition: all 0.3s;
      z-index: 10;

      &.has_input {
        background-color: @morejoy; // 有输入时变成橙色
        opacity: 1;
      }

      &:hover:not(:disabled) {
        opacity: 1;
        font-size: 38px;
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      .rotating {
        animation: rotate 1s linear infinite;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
